import React from "react";
import styled from "styled-components";
import { brand_orange_deep, brand_danger } from "../../constants/styles";
import { useToast } from "../../commons/Toast";

// Modal for reporting a comment. Submits to the legacy
// `PATCH /comments/:id` endpoint with `comment[reports]=<reason>` —
// the controller appends to a serialized hash keyed by the reporter.
//
// `apiPost` is wired with the right CSRF token; we use a manual
// `fetch` instead because Rails' update action accepts PATCH not POST,
// and our `apiPost`/`apiPut` helpers want a body shape we don't quite
// match. Using `fetch` here keeps the contract obvious.
const REASONS = [
  { value: "spam", label: "Spam" },
  { value: "irrelevant", label: "Tidak Relevan" },
  { value: "harassment", label: "Mengganggu / Pelecehan" },
  { value: "other", label: "Lainnya" },
];

function LessonReportModal({ commentId, onClose }) {
  const [reasonKey, setReasonKey] = React.useState("spam");
  const [details, setDetails] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const { showToast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const payload = `${REASONS.find((r) => r.value === reasonKey)?.label || "Spam"}${
      details ? ` — ${details}` : ""
    }`;
    try {
      const csrf =
        document.querySelector('meta[name="csrf-token"]')?.content || "";
      const res = await fetch(`/comments/${commentId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrf,
        },
        body: JSON.stringify({
          comment: { reports: payload },
        }),
      });
      if (res.status >= 200 && res.status < 400) {
        showToast({
          tone: "success",
          message: "Komentar dilaporkan. Terima kasih.",
        });
        onClose?.();
      } else {
        setError("Gagal mengirim laporan. Silakan coba lagi.");
      }
    } catch (err) {
      console.warn("[LessonReportModal] report failed", err);
      setError("Gagal mengirim laporan. Silakan coba lagi.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Backdrop
      role="dialog"
      aria-modal="true"
      aria-labelledby="report-title"
      onClick={onClose}
    >
      <Card onClick={(e) => e.stopPropagation()}>
        <Title id="report-title">Laporkan Komentar</Title>
        <Lede>Beritahu kami alasan komentar ini melanggar.</Lede>
        <form onSubmit={handleSubmit}>
          <RadioGroup>
            {REASONS.map((r) => (
              <Radio key={r.value}>
                <input
                  type="radio"
                  name="reason"
                  value={r.value}
                  checked={reasonKey === r.value}
                  onChange={() => setReasonKey(r.value)}
                />
                <span>{r.label}</span>
              </Radio>
            ))}
          </RadioGroup>
          <Textarea
            placeholder="Detail tambahan (opsional)"
            value={details}
            onChange={(e) => setDetails(e.target.value.slice(0, 280))}
            rows={3}
          />
          {error && <ErrorLine role="alert">{error}</ErrorLine>}
          <Buttons>
            <CancelButton type="button" onClick={onClose} disabled={busy}>
              Batal
            </CancelButton>
            <SubmitButton type="submit" disabled={busy}>
              {busy ? "Mengirim…" : "Kirim Laporan"}
            </SubmitButton>
          </Buttons>
        </form>
      </Card>
    </Backdrop>
  );
}

export default React.memo(LessonReportModal);

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 90;
  padding: 16px;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px 28px;
  width: min(440px, 100%);
  box-shadow: 0 24px 56px rgba(0, 0, 0, 0.32);
  [data-theme="dark"] & {
    background: #111827;
    color: #f3f4f6;
  }
`;

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 800;
  color: ${brand_orange_deep};
`;

const Lede = styled.p`
  margin: 0 0 14px;
  font-size: 13px;
  color: #6b7280;
  [data-theme="dark"] & {
    color: #cbd5e1;
  }
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
`;

const Radio = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  background: #f9fafb;
  font-size: 14px;

  &:hover {
    background: #fff7ed;
  }
  input {
    accent-color: ${brand_orange_deep};
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
    &:hover {
      background: rgba(216, 89, 7, 0.12);
    }
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 10px 12px;
  font-size: 13px;
  font-family: inherit;
  resize: vertical;
  min-height: 64px;

  &:focus-visible {
    outline: 2px solid ${brand_orange_deep};
    outline-offset: 2px;
  }

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.04);
    border-color: rgba(255, 255, 255, 0.1);
    color: #f3f4f6;
  }
`;

const ErrorLine = styled.div`
  margin: 8px 0;
  font-size: 12px;
  color: ${brand_danger};
`;

const Buttons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 14px;
`;

const CancelButton = styled.button`
  padding: 10px 18px;
  border-radius: 999px;
  background: transparent;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SubmitButton = styled.button`
  padding: 10px 22px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  border: none;
  font-weight: 700;
  font-size: 13px;
  cursor: pointer;

  &:hover {
    filter: brightness(0.95);
  }
  &:disabled {
    opacity: 0.6;
    cursor: wait;
  }
`;
